import React from 'react'
import {
  Dialog,
  DialogContent,
  IconButton,
  Box,
  Typography
} from '@mui/material'
import {
  Close,
  NavigateBefore,
  NavigateNext
} from '@mui/icons-material'

interface ImageOverlayProps {
  open: boolean
  onClose: () => void
  images: string[]
  currentImageIndex: number
  onImageChange: (index: number) => void
}

const ImageOverlay: React.FC<ImageOverlayProps> = ({
  open,
  onClose,
  images,
  currentImageIndex,
  onImageChange
}) => {
  const handlePrevious = () => {
    const newIndex = currentImageIndex === 0 ? images.length - 1 : currentImageIndex - 1
    onImageChange(newIndex)
  }

  const handleNext = () => {
    const newIndex = currentImageIndex === images.length - 1 ? 0 : currentImageIndex + 1
    onImageChange(newIndex)
  }

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Escape') onClose()
    if (event.key === 'ArrowLeft') handlePrevious()
    if (event.key === 'ArrowRight') handleNext()
  }

  if (!images.length) return null

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: {
          backgroundColor: 'rgba(0, 0, 0, 0.9)',
          border: 'none',
          boxShadow: 'none',
          overflow: 'hidden'
        }
      }}
      onKeyDown={handleKeyDown}
    >
      <DialogContent sx={{ p: 0, position: 'relative', minHeight: 500 }}>
        {/* Close Button */}
        <IconButton
          onClick={onClose}
          sx={{
            position: 'absolute',
            top: 16,
            right: 16,
            zIndex: 1,
            backgroundColor: 'rgba(255, 255, 255, 0.1)',
            color: 'white',
            '&:hover': {
              backgroundColor: 'rgba(255, 255, 255, 0.2)'
            }
          }}
        >
          <Close />
        </IconButton>

        {/* Navigation Buttons */}
        {images.length > 1 && (
          <>
            <IconButton
              onClick={handlePrevious}
              sx={{
                position: 'absolute',
                left: 16,
                top: '50%',
                transform: 'translateY(-50%)',
                zIndex: 1,
                backgroundColor: 'rgba(255, 255, 255, 0.1)',
                color: 'white',
                '&:hover': {
                  backgroundColor: 'rgba(255, 255, 255, 0.2)'
                }
              }}
            >
              <NavigateBefore />
            </IconButton>
            <IconButton
              onClick={handleNext}
              sx={{
                position: 'absolute',
                right: 16,
                top: '50%',
                transform: 'translateY(-50%)',
                zIndex: 1,
                backgroundColor: 'rgba(255, 255, 255, 0.1)',
                color: 'white',
                '&:hover': {
                  backgroundColor: 'rgba(255, 255, 255, 0.2)'
                }
              }}
            >
              <NavigateNext />
            </IconButton>
          </>
        )}

        {/* Main Image */}
        <Box
          component="img"
          src={images[currentImageIndex]}
          alt={`Image ${currentImageIndex + 1}`}
          sx={{
            width: '100%',
            height: '100%',
            objectFit: 'contain',
            maxHeight: '80vh'
          }}
          onError={(e: any) => {
            e.target.src = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="200" height="200"%3E%3Crect fill="%23444" width="200" height="200"/%3E%3Ctext x="50%25" y="50%25" text-anchor="middle" dy=".3em" fill="%23fff"%3EImage Not Available%3C/text%3E%3C/svg%3E'
          }}
        />

        {/* Image Counter */}
        {images.length > 1 && (
          <Box
            sx={{
              position: 'absolute',
              bottom: 16,
              left: '50%',
              transform: 'translateX(-50%)',
              backgroundColor: 'rgba(0, 0, 0, 0.7)',
              color: 'white',
              px: 2,
              py: 1,
              borderRadius: 1
            }}
          >
            <Typography variant="body2">
              {currentImageIndex + 1} / {images.length}
            </Typography>
          </Box>
        )}
      </DialogContent>
    </Dialog>
  )
}

export default ImageOverlay
